/**
 * Medicolegal service types used to categorise a case at creation.
 * Stored on `Case.service_type` server-side.
 */
export type ServiceTypeSlug =
  | "capacity_assessment"
  | "comcare"
  | "dva"
  | "fitness_for_duty"
  | "institutional_abuse"
  | "ip_tpd"
  | "medical_negligence"
  | "motor_vehicle"
  | "pain_suffering_quantum"
  | "public_liability"
  | "workers_compensation";

export type ServiceTypeOption = {
  value: ServiceTypeSlug;
  label: string;
};

export const SERVICE_TYPES: ServiceTypeOption[] = [
  { value: "capacity_assessment", label: "Capacity Assessment" },
  { value: "comcare", label: "Comcare" },
  { value: "dva", label: "DVA" },
  { value: "fitness_for_duty", label: "Fitness for Duty Assessments" },
  { value: "institutional_abuse", label: "Institutional Abuse" },
  { value: "ip_tpd", label: "IP/TPD Assessments" },
  { value: "medical_negligence", label: "Medical Negligence" },
  { value: "motor_vehicle", label: "Motor Vehicle Assessments" },
  {
    value: "pain_suffering_quantum",
    label: "Pain and Suffering Quantum Assessments",
  },
  { value: "public_liability", label: "Public Liability Assessments" },
  { value: "workers_compensation", label: "Workers Compensation Assessments" },
];

export const getServiceTypeLabel = (slug: string | null | undefined): string =>
  SERVICE_TYPES.find((s) => s.value === slug)?.label ?? "";

/**
 * Report Builder template name that corresponds to a service type (MDRS-141).
 *
 * There is no backend link between a case's service type and a template — the
 * 11 service-type templates (MDRS-118) are joined to the service slugs purely
 * by their display name matching the service-type label. This helper is the
 * single place that assumption lives, so callers (e.g. the Create Case form,
 * which auto-selects the template from the chosen service) don't hard-code it.
 *
 * Returns null for an unknown/blank slug so callers can no-op cleanly.
 */
export const getTemplateNameForServiceType = (
  slug: string | null | undefined
): string | null => getServiceTypeLabel(slug) || null;
